import { Controller } from "@hotwired/stimulus"
import { openModal } from "../../website/modal"

export default class extends Controller {
  static targets = [
    "addButton",
    "addCart",
    "buyBox",
    "prices",
    "quantity",
    "quantityInput",
    "shippingFees",
    "specialDeliveryCosts",
    "variantInput",
    "variantOption"
  ]
  static values = { addUrl: String, errorMessage: String, productId: Number, updateUrl: String }

  initialize() {
    this.variants = new Map()
  }

  connect() {
    this.element.dataset.productBuyBoxReady = "true"
  }

  disconnect() {
    this.variantRequest?.abort()
    this.cartRequest?.abort()
    delete this.element.dataset.productBuyBoxReady
    delete this.element.dataset.selectedVariantId
  }

  selectVariant(event) {
    const selectedOption = event.currentTarget

    this.variantOptionTargets.forEach(option => {
      const selected = option === selectedOption
      option.classList.toggle("active", selected)
      option.setAttribute("aria-pressed", selected.toString())
    })

    this.variantInputTarget.value = selectedOption.dataset.value
    this.variantInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  async updateVariant() {
    const variantId = this.variantInputTarget.value
    if (!variantId || variantId === "0") return

    this.clearVariantWarning()
    if (this.element.dataset.selectedVariantId === variantId) return

    const cachedVariant = this.variants.get(variantId)
    if (cachedVariant) {
      this.renderVariant(cachedVariant)
      this.element.dataset.selectedVariantId = variantId
      return
    }

    this.variantRequest?.abort()
    const request = new AbortController()
    this.variantRequest = request
    delete this.element.dataset.productBuyBoxError
    this.updateBusyState()

    try {
      const url = new URL(this.updateUrlValue, window.location.origin)
      url.searchParams.set("product_id", this.productIdValue)
      url.searchParams.set("variant_id", variantId)

      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: request.signal
      })
      if (!response.ok) throw new Error(`Variant update failed with status ${response.status}`)

      const variant = await response.json()
      this.variants.set(variantId, variant)
      this.renderVariant(variant)
      this.element.dataset.selectedVariantId = variantId
    } catch (error) {
      if (error.name !== "AbortError") this.element.dataset.productBuyBoxError = "true"
    } finally {
      if (this.variantRequest === request) {
        this.variantRequest = null
        this.updateBusyState()
      }
    }
  }

  async addToCart() {
    const variantId = this.hasVariantInputTarget ? this.variantInputTarget.value : null
    if (!variantId || variantId === "0") {
      this.showVariantWarning()
      return
    }

    this.cartRequest?.abort()
    const request = new AbortController()
    this.cartRequest = request
    this.setCartLoading(true)

    try {
      const response = await fetch(this.addUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: new URLSearchParams({
          quantity: this.hasQuantityInputTarget ? this.quantityInputTarget.value : "1",
          variant_id: variantId
        }),
        signal: request.signal
      })
      if (!response.ok) throw new Error(`Unable to add product (${response.status})`)

      this.handleCartResponse(await response.json(), variantId)
    } catch (error) {
      if (error.name !== "AbortError") this.showCartError()
    } finally {
      if (this.cartRequest === request) this.setCartLoading(false)
    }
  }

  handleCartResponse(data, variantId) {
    if (data.type === "multi_sale") {
      this.prepareMultiSaleModal(variantId)
      return
    }

    if (data.message) {
      this.showCartError(data.message)
      return
    }

    this.replaceHeaderCart(data.cart)
    this.variants.clear()
    delete this.element.dataset.selectedVariantId
    this.buyBoxTarget.outerHTML = data.buy_box
    this.addCartTarget.innerHTML = data.add_cart
    globalThis.countdown?.(data.cart_remaining_seconds)
    window.scrollTo({ top: 0, behavior: "smooth" })
    openModal("modal-add-cart")
  }

  replaceHeaderCart(html) {
    const headerCarts = Array.from(document.querySelectorAll("#header-cart"))
      .filter(headerCart => !headerCart.parentElement?.closest("#header-cart"))

    headerCarts.forEach(headerCart => {
      headerCart.innerHTML = html
      headerCart.querySelectorAll("script").forEach(script => script.remove())
    })
  }

  prepareMultiSaleModal(variantId) {
    const modal = document.querySelector("#modal-multi-sale")
    if (!modal) return

    modal.querySelector("#rebuild_variant_id").value = variantId
    modal.querySelector("#rebuild_quantity").value = this.hasQuantityInputTarget ? this.quantityInputTarget.value : "1"
    openModal("modal-multi-sale")
  }

  showCartError(message) {
    const modal = document.querySelector("#modal")
    if (!modal) return

    const title = modal.querySelector(".modal-header .title")
    const body = modal.querySelector(".modal-body")
    if (title) title.textContent = ""
    if (body) body.textContent = message || this.errorMessageValue
    openModal("modal")
  }

  setCartLoading(loading) {
    if (!loading) this.cartRequest = null
    this.updateBusyState()
    if (this.hasAddButtonTarget) this.addButtonTarget.disabled = loading
  }

  updateBusyState() {
    this.buyBoxTarget.toggleAttribute("aria-busy", Boolean(this.variantRequest || this.cartRequest))
  }

  renderVariant(data) {
    this.pricesTarget.outerHTML = data.prices_html
    this.updateQuantity(data.quantity_limit, data.sold_out_label)

    if (this.hasShippingFeesTarget) {
      this.shippingFeesTarget.innerHTML = data.shipping_fees_html
    }

    if (this.hasSpecialDeliveryCostsTarget) {
      this.specialDeliveryCostsTarget.hidden = !(Number(data.delivery_costs) > 20)
      const deliveryCosts = this.specialDeliveryCostsTarget.querySelector("#delivery_costs")
      if (deliveryCosts) deliveryCosts.textContent = data.delivery_costs_label
    }
  }

  updateQuantity(quantityLimit, soldOutLabel) {
    if (!this.hasQuantityTarget || !this.hasQuantityInputTarget) return

    const dropdown = this.quantityTarget.querySelector('[data-controller~="website--dropdown-select"]')
    const trigger = dropdown?.querySelector('[data-website--dropdown-select-target="trigger"]')
    const list = dropdown?.querySelector(".dropdownList")
    if (!dropdown || !trigger || !list) return

    const stock = Number(quantityLimit)
    const maximum = Number.isFinite(stock) ? Math.min(Math.max(Math.floor(stock), 0), 5) : 0
    list.replaceChildren(...this.quantityOptions(maximum))

    dropdown.classList.toggle("disabled", maximum === 0)
    trigger.disabled = maximum === 0
    this.quantityInputTarget.value = maximum > 0 ? "1" : "0"
    this.quantityInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    if (maximum === 0) trigger.textContent = soldOutLabel

    this.quantityTarget.classList.remove("hide")
  }

  quantityOptions(maximum) {
    return Array.from({ length: maximum }, (_, index) => {
      const quantity = (index + 1).toString()
      const option = document.createElement("li")
      option.classList.toggle("active", index === 0)
      option.setAttribute("role", "option")
      option.setAttribute("tabindex", "0")
      option.setAttribute("aria-selected", (index === 0).toString())
      option.setAttribute("data-value", quantity)
      option.setAttribute("data-text", quantity)
      option.setAttribute("data-website--dropdown-select-target", "option")
      option.setAttribute(
        "data-action",
        "click->website--dropdown-select#select keydown.enter->website--dropdown-select#select keydown.space->website--dropdown-select#select"
      )
      option.textContent = quantity
      return option
    })
  }

  clearVariantWarning() {
    this.buyBoxTarget.querySelector("#missing-variant-alert")?.removeAttribute("style")
    this.buyBoxTarget.querySelector(".variants .dropdownContainer")?.classList.remove("warning")
    this.variantOptionTargets.forEach(option => option.classList.remove("warning"))
  }

  showVariantWarning() {
    const dropdown = this.buyBoxTarget.querySelector(".variants .dropdownContainer")
    if (dropdown) {
      dropdown.classList.add("warning")
      dropdown.focus()
      return
    }

    this.variantOptionTargets.forEach(option => option.classList.add("warning"))
    const warning = this.buyBoxTarget.querySelector("#missing-variant-alert")
    if (warning) warning.style.display = "block"
  }
}
